import {
  type Observable,
  merge,
  race,
  map,
  share,
  take,
  find,
  takeUntil,
} from 'rxjs'
import type { MessageStatus } from './message-status'

/**
 * Stream for message status deltas.
 *
 * @param statusChanges$ the status change stream for the message, belonging to a single user
 * @param messageRemoved$ a stream that emits a value when the message is removed
 */
export function messageStatusDeltaStream(
  statusChanges$: Observable<MessageStatus>,
  messageRemoved$: Observable<unknown>,
) {
  // Since we subscribe twice to this one, make sure it's a shared observable.
  // We only need the first emission.
  const sharedRemove$ = messageRemoved$.pipe(share(), take(1))

  // Observables for read and delivered statuses.
  // We only need the first emission of each.
  const delivered$ = statusChanges$.pipe(
    find((s) => s.deliveryStatus === 'Delivered'),
  )
  const read$ = statusChanges$.pipe(find((s) => s.deliveryStatus === 'Read'))

  // Map delivered and reads to deltas.
  // We only want to decrement when either a read or a removal event comes in,
  // but not both.
  const readOrRemoved$ = race(read$, sharedRemove$).pipe(share())
  return merge(
    // Increment the delta when a delivered event comes in.
    delivered$.pipe(
      map(() => 1),
      takeUntil(readOrRemoved$),
    ),
    // Decrement the delta when a read event comes in, or when the message is removed.
    readOrRemoved$.pipe(map(() => -1)),
  )
}
